import { Step } from 'react-joyride-react-19'
import { 
  Paperclip, Upload, Image, FileText, Trash2, AlertTriangle, Layout, File, 
  Sparkles, Zap, Eye, Download, Filter, Search, Grid, List, 
  CheckCircle, Clock, Shield, Users, Rocket, Star, Heart,
  MousePointer2, Layers, Archive, RefreshCw, Settings, 
  ArrowRight, ChevronRight, Crown, Target, Wand2, UserCheck,
  Database, Plus, Edit, MapPin, BarChart3, Gauge, TrendingUp,
  AlertCircle, History, Building, Activity, LayoutTemplate, Settings2
} from 'lucide-react'

/**
 * 🎯 CONFIGURAÇÃO CENTRALIZADA DE TOURS
 * 
 * Este arquivo centraliza todos os tours do sistema para:
 * - Melhor organização e manutenção
 * - Facilitar reutilização de steps
 * - Controle centralizado de IDs e conteúdo
 * - Facilitar tradução futura
 */

// Identificadores únicos dos tours
export const TOUR_IDS = {
  CLAIM_DETAILS_ATTACHMENTS: 'claim-details-attachments',
  TRASH_PAGE_TABS: 'trash-page-tabs',
  PRESTADORES_MANAGER: 'prestadores-manager',
  MAP_PRESTADORES: 'map-prestadores',
  PHOTO_CAPTIONS: 'photo-captions',
  CLAIMS_FILTERS: 'claims-filters',
  COLUMN_CONFIGURATOR: 'column-configurator',
  DISTRIBUTION_BUTTON: 'distribution-button',
  TRACKS_MENU: 'tracks-menu',
  // Adicionar novos tours aqui conforme necessário
} as const

export type TourId = keyof typeof TOUR_IDS

// Interface para definição de tour
export interface TourDefinition {
  id: string
  name: string
  description: string
  steps: Step[]
  autoStart?: boolean
  conditions?: {
    // Condições para auto-iniciar o tour
    requiresData?: boolean
    minWaitTime?: number
  }
}

/**
 * 📎 TOUR: Sistema de Anexos no ClaimDetails
 * Explica como funciona o novo sistema de anexos
 */
export const claimDetailsAttachmentsTour: TourDefinition = {
  id: TOUR_IDS.CLAIM_DETAILS_ATTACHMENTS,
  name: 'Sistema de Anexos',
  description: 'Apresenta o novo sistema de anexos do ClaimDetails',
  autoStart: true,
  conditions: {
    requiresData: false, // Removido - tour aparece sempre
    minWaitTime: 1500
  },
  steps: [
    {
      target: '[data-tour="attachments-section"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
                <Paperclip className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Sistema de Anexos
              </h3>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Novo e melhorado!</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Bem-vindo ao novo sistema de anexos!</strong> Uma interface moderna e intuitiva para gerenciar todos os arquivos do sinistro.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Eye className="w-3 h-3 text-keffe-clay" />
              <span>Cards visuais</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <Filter className="w-3 h-3 text-green-600" />
              <span>Filtros avançados</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Download className="w-3 h-3 text-purple-600" />
              <span>Download rápido</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Shield className="w-3 h-3 text-orange-600" />
              <span>Permissões</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <Target className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 dark:text-green-200">
              <strong>Dica:</strong> Clique em qualquer arquivo para uma visualização rápida!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: '[data-tour="attachments-upload-btn"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 p-2 rounded-lg">
                <Upload className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Upload Inteligente
              </h3>
              <div className="flex items-center gap-2">
                <Zap className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Rápido e fácil</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Wand2 className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Auto-classificação</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Adicione arquivos de forma inteligente!</strong> Nosso sistema detecta automaticamente o tipo e organiza os arquivos para você.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-keffe-clay" />
                <span className="text-sm font-medium">Documentos</span>
              </div>
              <span className="text-xs bg-keffe-cream dark:bg-keffe-dark-brown px-2 py-1 rounded">PDF, DOC, XLS</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium">Imagens</span>
              </div>
              <span className="text-xs bg-purple-100 dark:bg-purple-900 px-2 py-1 rounded">JPG, PNG, GIF</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <MousePointer2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Pro Tip:</strong> Arraste os arquivos diretamente do seu computador para upload instantâneo!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="attachments-gallery-btn"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-purple-500 to-pink-600 p-2 rounded-lg">
                <Image className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Galeria Premium
              </h3>
              <div className="flex items-center gap-2">
                <Star className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Visualização avançada</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Crown className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Recursos premium</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Explore a galeria completa!</strong> Uma experiência visual rica para navegar e gerenciar todos os anexos.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Grid className="w-3 h-3 text-purple-600" />
              <span>Vista em grade</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-pink-50 dark:bg-pink-950 rounded border border-pink-200 dark:border-pink-800">
              <List className="w-3 h-3 text-pink-600" />
              <span>Vista em lista</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-indigo-50 dark:bg-indigo-950 rounded border border-indigo-200 dark:border-indigo-800">
              <Search className="w-3 h-3 text-indigo-600" />
              <span>Busca avançada</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-cyan-50 dark:bg-cyan-950 rounded border border-cyan-200 dark:border-cyan-800">
              <Layers className="w-3 h-3 text-cyan-600" />
              <span>Filtros múltiplos</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg border border-purple-200 dark:border-purple-800">
            <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-sm text-purple-800 dark:text-purple-200">
              <strong>Preview Mágico:</strong> Clique duas vezes em qualquer imagem para visualização em tela cheia!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="attachments-cards"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-orange-500 to-red-600 p-2 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Cards Inteligentes
              </h3>
              <div className="flex items-center gap-2">
                <Heart className="w-3 h-3 text-red-500" />
                <span className="text-xs text-muted-foreground">Design moderno</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Rocket className="w-3 h-3 text-orange-500" />
                <span className="text-xs text-muted-foreground">Ações rápidas</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Cards informativos e elegantes!</strong> Cada anexo ganha vida com informações completas e ações intuitivas.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-keffe-cream to-keffe-sandstone/50 dark:from-keffe-dark-brown dark:to-keffe-espresso/50 rounded-lg border border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-keffe-clay" />
                <span className="text-sm font-medium">Preview Visual</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-green-600">Ativo</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950 dark:to-green-950 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium">Info Técnicas</span>
              </div>
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900 px-2 py-1 rounded">Tamanho, Data, Tipo</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 rounded-lg border border-orange-200 dark:border-orange-800">
            <Users className="w-4 h-4 text-orange-600 flex-shrink-0" />
            <span className="text-sm text-orange-800 dark:text-orange-200">
              <strong>Permissões Visuais:</strong> Veja instantaneamente quem pode acessar cada arquivo!
            </span>
          </div>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    }
  ]
}

/**
 * 🗑️ TOUR: Sistema de Abas na Lixeira
 * Explica o novo sistema de abas para sinistros e anexos
 */
export const trashPageTabsTour: TourDefinition = {
  id: TOUR_IDS.TRASH_PAGE_TABS,
  name: 'Lixeira com Abas',
  description: 'Apresenta o novo sistema de abas da lixeira',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 1000
  },
  steps: [
    {
      target: '[data-tour="trash-header"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-red-500 to-pink-600 p-2 rounded-lg">
                <Trash2 className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">
                Lixeira Inteligente
              </h3>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Restauração fácil</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Archive className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Organizada</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Nova lixeira redesenhada!</strong> Agora com abas organizadas para melhor controle de itens removidos.
          </p>
          
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950 rounded-xl border-2 border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500 rounded-full blur opacity-30"></div>
                <div className="relative bg-amber-500 p-1.5 rounded-full">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-1">Atenção Importante</h4>
                <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
                  Items aqui podem ser <strong>restaurados</strong> ou <strong>excluídos permanentemente</strong>. 
                  Exclusões permanentes não podem ser desfeitas!
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="trash-tabs"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-2 rounded-lg">
                <Layout className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Abas Organizadas
              </h3>
              <div className="flex items-center gap-2">
                <Layers className="w-3 h-3 text-indigo-500" />
                <span className="text-xs text-muted-foreground">Separação inteligente</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Target className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Busca fácil</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Organização perfeita!</strong> Duas abas distintas para separar sinistros e anexos removidos.
          </p>
          
          <div className="space-y-3">
            <div className="p-3 bg-gradient-to-r from-keffe-cream to-keffe-sandstone/50 dark:from-keffe-dark-brown dark:to-keffe-espresso/50 rounded-lg border-2 border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-3">
                <div className="bg-keffe-dark-brown p-2 rounded-lg">
                  <File className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-keffe-espresso dark:text-keffe-sandstone">Aba Sinistros</h4>
                  <p className="text-sm text-keffe-clay dark:text-keffe-sandstone">Sinistros removidos do sistema</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg border-2 border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 p-2 rounded-lg">
                  <Paperclip className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-800 dark:text-purple-200">Aba Anexos</h4>
                  <p className="text-sm text-purple-600 dark:text-purple-300">Arquivos removidos de sinistros</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 dark:text-green-200">
              <strong>Navegação Intuitiva:</strong> Clique nas abas para alternar entre tipos de conteúdo!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="claims-tab"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-cyan-600 p-2 rounded-lg">
                <File className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Gestão de Sinistros
              </h3>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Restauração</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Clock className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Auto-limpeza</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Controle total dos sinistros removidos!</strong> Visualize, restaure ou gerencie definitivamente.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <RefreshCw className="w-3 h-3 text-green-600" />
              <span>Restaurar</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <Trash2 className="w-3 h-3 text-red-600" />
              <span>Excluir final</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Filter className="w-3 h-3 text-purple-600" />
              <span>Filtros</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-amber-50 dark:bg-amber-950 rounded border border-amber-200 dark:border-amber-800">
              <Clock className="w-3 h-3 text-amber-600" />
              <span>365 dias</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-keffe-cream to-keffe-sandstone/50 dark:from-keffe-dark-brown dark:to-keffe-olive/50 rounded-lg border border-keffe-sandstone dark:border-keffe-olive">
            <Shield className="w-4 h-4 text-keffe-clay flex-shrink-0" />
            <span className="text-sm text-keffe-espresso dark:text-keffe-sandstone">
              <strong>Segurança:</strong> Apenas administradores podem fazer exclusões permanentes!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="attachments-tab"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-purple-500 to-violet-600 p-2 rounded-lg">
                <Paperclip className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                Gestão de Anexos
              </h3>
              <div className="flex items-center gap-2">
                <Eye className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Preview</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Archive className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Restauração</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Recupere seus arquivos!</strong> Gerencie anexos removidos com preview e restauração inteligente.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Eye className="w-3 h-3 text-keffe-clay" />
              <span>Preview</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <RefreshCw className="w-3 h-3 text-green-600" />
              <span>Restaurar</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Filter className="w-3 h-3 text-orange-600" />
              <span>Filtros</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <Trash2 className="w-3 h-3 text-red-600" />
              <span>Excluir</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950 dark:to-violet-950 rounded border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium">Imagens</span>
              </div>
              <span className="text-xs bg-purple-100 dark:bg-purple-900 px-2 py-1 rounded">JPG, PNG</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950 dark:to-blue-950 rounded border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">Documentos</span>
              </div>
              <span className="text-xs bg-indigo-100 dark:bg-indigo-900 px-2 py-1 rounded">PDF, DOC</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950 dark:to-purple-950 rounded-lg border border-violet-200 dark:border-violet-800">
            <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <span className="text-sm text-violet-800 dark:text-violet-200">
              <strong>Preview Inteligente:</strong> Visualize arquivos antes de restaurar para ter certeza!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
    }
  ]
}

/**
 * 👥 TOUR: Sistema de Gestão de Prestadores
 * Explica como usar a interface completa de prestadores
 */
export const prestadoresManagerTour: TourDefinition = {
  id: TOUR_IDS.PRESTADORES_MANAGER,
  name: 'Gestão de Prestadores',
  description: 'Apresenta o sistema completo de gestão de prestadores',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 1200
  },
  steps: [
    {
      target: '[data-tour="prestadores-header"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-lg">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Sistema de Prestadores
              </h3>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Gestão completa e moderna!</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Bem-vindo ao sistema de prestadores!</strong> Uma interface poderosa para gerenciar milhares de prestadores com performance otimizada.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-emerald-50 dark:bg-emerald-950 rounded border border-emerald-200 dark:border-emerald-800">
              <Database className="w-3 h-3 text-emerald-600" />
              <span>Importação CSV</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Search className="w-3 h-3 text-keffe-clay" />
              <span>Busca inteligente</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Filter className="w-3 h-3 text-purple-600" />
              <span>Filtros avançados</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Gauge className="w-3 h-3 text-orange-600" />
              <span>Alta performance</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <Target className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 dark:text-green-200">
              <strong>Performance:</strong> Sistema otimizado para lidar com milhares de registros!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: '[data-tour="prestadores-actions"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 p-2 rounded-lg">
                <Rocket className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Ações Principais
              </h3>
              <div className="flex items-center gap-2">
                <Upload className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Importação massiva</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Plus className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Criação individual</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Duas formas de adicionar prestadores!</strong> Importação em massa via CSV ou criação individual com formulário completo.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950 dark:to-green-950 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium">Import CSV</span>
              </div>
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900 px-2 py-1 rounded">Milhares de uma vez</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-keffe-cream to-keffe-sandstone/50 dark:from-keffe-dark-brown dark:to-keffe-olive/50 rounded-lg border border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-keffe-clay" />
                <span className="text-sm font-medium">Novo Prestador</span>
              </div>
              <span className="text-xs bg-keffe-cream dark:bg-keffe-dark-brown px-2 py-1 rounded">Formulário completo</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <TrendingUp className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Dica:</strong> Use CSV para importações grandes e o formulário para ajustes precisos!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="prestadores-search"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-purple-500 to-violet-600 p-2 rounded-lg">
                <Search className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                Busca Inteligente
              </h3>
              <div className="flex items-center gap-2">
                <Zap className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Super otimizada</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Wand2 className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Cache inteligente</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Sistema de busca ultra-rápido!</strong> Com cache inteligente, debounce otimizado e filtros avançados para encontrar qualquer prestador.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Search className="w-3 h-3 text-purple-600" />
              <span>Nome, CPF, CNPJ</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <MapPin className="w-3 h-3 text-keffe-clay" />
              <span>Estado, Cidade</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <Filter className="w-3 h-3 text-green-600" />
              <span>Status, Perfil</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Gauge className="w-3 h-3 text-orange-600" />
              <span>Cache rápido</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <Rocket className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="text-sm text-indigo-800 dark:text-indigo-200">
              <strong>Performance:</strong> Busca em 800ms + cache instantâneo para resultados já pesquisados!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="prestadores-stats"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-orange-500 to-red-600 p-2 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Estatísticas Dinâmicas
              </h3>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Tempo real</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Eye className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Visão geral</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Informações sempre atualizadas!</strong> Veja estatísticas em tempo real dos prestadores cadastrados e ativos.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">Prestadores Ativos</span>
              </div>
              <span className="text-xs bg-green-100 dark:bg-green-900 px-2 py-1 rounded">Operacionais</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium">Prestadores Inativos</span>
              </div>
              <span className="text-xs bg-orange-100 dark:bg-orange-900 px-2 py-1 rounded">Suspensos</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950 dark:to-blue-950 rounded-lg border border-cyan-200 dark:border-cyan-800">
            <BarChart3 className="w-4 h-4 text-cyan-600 flex-shrink-0" />
            <span className="text-sm text-cyan-800 dark:text-cyan-200">
              <strong>Dashboard:</strong> Números atualizados a cada busca e filtro aplicado!
            </span>
          </div>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="prestadores-table"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-cyan-500 to-blue-600 p-2 rounded-lg">
                <Layout className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Tabela Inteligente
              </h3>
              <div className="flex items-center gap-2">
                <Layers className="w-3 h-3 text-cyan-500" />
                <span className="text-xs text-muted-foreground">Informações completas</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Star className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Ações rápidas</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Visualização completa e organizada!</strong> Cada prestador exibido com informações essenciais e badges informativos.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <UserCheck className="w-3 h-3 text-keffe-clay" />
              <span>Dados pessoais</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <MapPin className="w-3 h-3 text-green-600" />
              <span>Localização</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Shield className="w-3 h-3 text-purple-600" />
              <span>Perfis ativos</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <CheckCircle className="w-3 h-3 text-orange-600" />
              <span>Status ativo</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-keffe-cream to-keffe-sandstone/50 dark:from-keffe-dark-brown dark:to-keffe-olive/50 rounded-lg border border-keffe-sandstone dark:border-keffe-olive">
            <MousePointer2 className="w-4 h-4 text-keffe-clay flex-shrink-0" />
            <span className="text-sm text-keffe-espresso dark:text-keffe-sandstone">
              <strong>Interativo:</strong> Clique nos badges para ver detalhes e nas ações para gerenciar!
            </span>
          </div>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="prestadores-actions-row"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-2 rounded-lg">
                <Settings className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Ações de Gestão
              </h3>
              <div className="flex items-center gap-2">
                <Eye className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Visualização</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Edit className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Edição</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Trash2 className="w-3 h-3 text-red-500" />
                <span className="text-xs text-muted-foreground">Remoção</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Controle total sobre cada prestador!</strong> Três ações principais para gerenciar completamente o cadastro.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-keffe-clay" />
                <span className="text-sm font-medium">Visualizar</span>
              </div>
              <span className="text-xs bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">Detalhes completos</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <Edit className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">Editar</span>
              </div>
              <span className="text-xs bg-green-100 dark:bg-green-900 px-2 py-1 rounded">Formulário completo</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium">Remover</span>
              </div>
              <span className="text-xs bg-red-100 dark:bg-red-900 px-2 py-1 rounded">Confirmação segura</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950 dark:to-purple-950 rounded-lg border border-violet-200 dark:border-violet-800">
            <Shield className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <span className="text-sm text-violet-800 dark:text-violet-200">
              <strong>Segurança:</strong> Todas as ações são protegidas e requerem confirmação para mudanças!
            </span>
          </div>
        </div>
      ),
      placement: 'left',
      spotlightClicks: true,
    }
  ]
}

/**
 * 🗺️ TOUR: Mapa de Prestadores
 * Explica como usar o mapa interativo de prestadores
 */
export const mapPrestadoresTour: TourDefinition = {
  id: TOUR_IDS.MAP_PRESTADORES,
  name: 'Mapa de Prestadores',
  description: 'Aprenda a usar o mapa interativo para visualizar e pesquisar prestadores geograficamente',
  autoStart: true,
  conditions: {
    requiresData: true,
    minWaitTime: 1000
  },
  steps: [
    {
      target: '[data-tour="map-header"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Mapa de Prestadores
              </h3>
              <div className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-emerald-500" />
                <span className="text-xs text-muted-foreground">Visualização Geográfica</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Bem-vindo ao Mapa de Prestadores!</strong> Aqui você pode visualizar todos os prestadores cadastrados que possuem coordenadas geográficas em um mapa interativo.
          </p>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <Eye className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="text-sm text-emerald-800 dark:text-emerald-200">
              <strong>Dica:</strong> Clique nos marcadores para ver detalhes de cada prestador!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="map-filters"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-cyan-600 p-2 rounded-lg">
                <Filter className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Filtros Inteligentes
              </h3>
              <div className="flex items-center gap-2">
                <Search className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Busca</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <MapPin className="w-3 h-3 text-green-500" />
                <span className="text-xs text-muted-foreground">Estados</span>
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <Users className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Perfis</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Filtros poderosos para encontrar exatamente o que procura!</strong> Use busca por texto, filtre por estado, status ou perfis específicos.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Search className="w-3 h-3 text-keffe-clay" />
              <span className="text-xs">Nome, CPF, CNPJ, cidade</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <MapPin className="w-3 h-3 text-green-600" />
              <span className="text-xs">Todos os estados do Brasil</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Users className="w-3 h-3 text-purple-600" />
              <span className="text-xs">Regulador, Vistoriador, etc.</span>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="map-stats"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-amber-500 to-orange-600 p-2 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Estatísticas em Tempo Real
              </h3>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Dados Atualizados</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Números importantes sempre à vista!</strong> Veja quantos prestadores estão no mapa, total geral, ativos e inativos.
          </p>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <div className="text-lg font-bold text-keffe-clay">No Mapa</div>
              <div className="text-xs text-muted-foreground">Com coordenadas</div>
            </div>
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-950 rounded border border-gray-200 dark:border-gray-800">
              <div className="text-lg font-bold text-gray-600">Total</div>
              <div className="text-xs text-muted-foreground">Cadastrados</div>
            </div>
            <div className="text-center p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <div className="text-lg font-bold text-green-600">Ativos</div>
              <div className="text-xs text-muted-foreground">Funcionais</div>
            </div>
            <div className="text-center p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <div className="text-lg font-bold text-red-600">Inativos</div>
              <div className="text-xs text-muted-foreground">Pausados</div>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="map-container"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-2 rounded-lg">
                <Layers className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Mapa Interativo
              </h3>
              <div className="flex items-center gap-2">
                <MousePointer2 className="w-3 h-3 text-indigo-500" />
                <span className="text-xs text-muted-foreground">Clique e navegue</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Seu mapa inteligente e responsivo!</strong> Use o mouse para navegar, zoom para aproximar e clique nos marcadores para ver detalhes.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-indigo-50 dark:bg-indigo-950 rounded border border-indigo-200 dark:border-indigo-800">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-xs">Marcadores azuis = Prestadores ativos</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-xs">Marcadores vermelhos = Prestadores inativos</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <div className="w-3 h-3 bg-orange-500 rounded-full border border-white"></div>
              <span className="text-xs">Agrupamento automático de marcadores próximos</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <Target className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="text-sm text-indigo-800 dark:text-indigo-200">
              <strong>Explore:</strong> Clique nos marcadores para abrir o painel de detalhes!
            </span>
          </div>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
  ],
}

/**
 * 🤖 TOUR: Gerador de Legendas com IA
 * Explica o sistema completo de geração de legendas para fotos de vistoria
 */
export const photoCaptionsTour: TourDefinition = {
  id: TOUR_IDS.PHOTO_CAPTIONS,
  name: 'Gerador de Legendas com IA',
  description: 'Aprenda a usar o sistema inteligente de geração de legendas para fotos',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 1500
  },
  steps: [
    {
      target: 'body',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Gerador de Legendas com IA
              </h3>
              <div className="flex items-center gap-1">
                <Wand2 className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Sistema completo e inteligente!</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Bem-vindo!</strong> Este sistema usa IA para transformar suas fotos de vistoria em descrições profissionais automaticamente.
          </p>
          
          <div className="space-y-3">
            <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <FileText className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-orange-800 dark:text-orange-200">1. Vincule ao Sinistro</h4>
                  <p className="text-xs text-orange-700 dark:text-orange-300">Primeiro, selecione o sinistro no card laranja acima</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <Upload className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-green-800 dark:text-green-200">2. Faça Upload das Fotos</h4>
                  <p className="text-xs text-green-700 dark:text-green-300">Arraste fotos ou clique para selecionar - até 300 fotos de 10MB</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <Wand2 className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-200">3. Processe com IA</h4>
                  <p className="text-xs text-blue-700 dark:text-blue-300">Clique em &ldquo;Processar&rdquo; e a IA gera legendas automaticamente em background</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <Eye className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-purple-800 dark:text-purple-200">4. Revise e Gere PDF</h4>
                  <p className="text-xs text-purple-700 dark:text-purple-300">Na aba &ldquo;Histórico&rdquo;, revise as legendas e gere o documento final</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <Rocket className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Dica:</strong> O processamento roda em background. Você pode navegar livremente!
            </span>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    }
  ]
}

/**
 * 🔍 TOUR: Filtros Avançados de Sinistros
 * Explica os novos filtros por tipo de produto e cobertura
 */
export const claimsFiltersTour: TourDefinition = {
  id: TOUR_IDS.CLAIMS_FILTERS,
  name: 'Filtros Avançados',
  description: 'Aprenda a filtrar sinistros por produto e cobertura',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 1500
  },
  steps: [
    {
      target: '[data-tour="claims-filters"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Filtros Avançados
              </h3>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Nova funcionalidade!</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Novos filtros poderosos!</strong> Agora você pode refinar sua busca de sinistros filtrando por tipo de produto e tipo de cobertura.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Building className="w-3 h-3 text-keffe-clay" />
              <span>Tipo de produto</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Shield className="w-3 h-3 text-purple-600" />
              <span>Tipo de cobertura</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
              <Filter className="w-3 h-3 text-green-600" />
              <span>Busca refinada</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Zap className="w-3 h-3 text-orange-600" />
              <span>Resultados rápidos</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <Target className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 dark:text-green-200">
              <strong>Dica:</strong> Combine múltiplos filtros para encontrar exatamente o que procura!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: '[data-tour="claims-product-filter"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-cyan-600 p-2 rounded-lg">
                <Building className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Filtro por Produto
              </h3>
              <div className="flex items-center gap-2">
                <Filter className="w-3 h-3 text-keffe-clay" />
                <span className="text-xs text-muted-foreground">Refine por categoria</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Filtre por tipo de produto!</strong> Selecione entre os produtos disponíveis para visualizar apenas os sinistros relacionados.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-keffe-clay" />
                <span className="text-sm font-medium">Exemplos</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground pl-2">
              • Residencial<br />
              • Empresarial<br />
              • Auto<br />
              • E mais...
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <MousePointer2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Clique aqui</strong> para abrir a lista de produtos disponíveis!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="claims-coverage-filter"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-purple-500 to-pink-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Filtro por Cobertura
              </h3>
              <div className="flex items-center gap-2">
                <Filter className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-muted-foreground">Especifique o tipo</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Filtre por tipo de cobertura!</strong> Escolha o tipo de cobertura específico para ver sinistros relacionados (incêndio, roubo, danos elétricos, etc).
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <AlertTriangle className="w-3 h-3 text-red-600" />
              <span>Incêndio</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Zap className="w-3 h-3 text-orange-600" />
              <span>Danos Elétricos</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Shield className="w-3 h-3 text-purple-600" />
              <span>Roubo/Furto</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-blue-50 dark:bg-blue-950 rounded border border-blue-200 dark:border-blue-800">
              <Activity className="w-3 h-3 text-blue-600" />
              <span>E muitos mais</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <Sparkles className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="text-sm text-indigo-800 dark:text-indigo-200">
              <strong>Combine:</strong> Use produto + cobertura juntos para busca ultra-precisa!
            </span>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightClicks: true,
    }
  ]
}

/**
 * ⚙️ TOUR: Configuração de Colunas
 * Ensina o usuário a reordenar colunas da tabela
 */
export const columnConfiguratorTour: TourDefinition = {
  id: TOUR_IDS.COLUMN_CONFIGURATOR,
  name: 'Configuração de Colunas',
  description: 'Aprenda a personalizar a ordem das colunas nas tabelas',
  autoStart: true,
  conditions: {
    requiresData: true,
    minWaitTime: 1500
  },
  steps: [
    {
      target: '[data-tour="claims-column-configurator"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-indigo-500 to-blue-600 p-2 rounded-lg">
                <Settings2 className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                Configure suas Colunas
              </h3>
              <div className="flex items-center gap-2">
                <LayoutTemplate className="w-3 h-3 text-indigo-500" />
                <span className="text-xs text-muted-foreground">Personalize sua visão</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Deixe a tabela com a sua cara!</strong> Clique aqui para reordenar as colunas da forma que for melhor para o seu trabalho.
          </p>
          
          <div className="p-3 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950 dark:to-blue-950 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <p className="text-xs text-indigo-700 dark:text-indigo-300">
              A ordem que você escolher ficará salva automaticamente na sua conta e será mantida nos próximos acessos.
            </p>
          </div>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
    }
  ]
}

/**
 * 👥 TOUR: Botão de Distribuição no Header
 * Avisa admins sobre o novo botão de acesso à distribuição
 */
export const distributionButtonTour: TourDefinition = {
  id: TOUR_IDS.DISTRIBUTION_BUTTON,
  name: 'Acesso à Distribuição',
  description: 'Novo botão de acesso à distribuição no header',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 2000
  },
  steps: [
    {
      target: '[data-tour="distribution-button"]',
      content: (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-1.5 rounded-lg">
              <Users className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-lg font-bold text-foreground">Distribuição Disponível Aqui</h3>
          </div>
          
          <p className="text-sm leading-relaxed">
            Agora você pode <strong>acessar a distribuição de sinistros</strong> diretamente por este botão no header.
          </p>
          
          <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950 dark:to-blue-950 rounded-lg border border-cyan-200 dark:border-cyan-800">
            <p className="text-xs text-cyan-700 dark:text-cyan-300">
              O número ao lado mostra quantos sinistros estão aguardando distribuição.
            </p>
          </div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
    }
  ]
}

/**
 * ⚡ TOUR: Menu de Tracks (Fast Track e Super Fast Track)
 * Apresenta o novo menu lateral para acesso rápido aos tracks especiais
 */
export const tracksMenuTour: TourDefinition = {
  id: TOUR_IDS.TRACKS_MENU,
  name: 'Menu de Tracks',
  description: 'Novo menu lateral para acesso rápido aos tracks especiais',
  autoStart: true,
  conditions: {
    requiresData: false,
    minWaitTime: 1500
  },
  steps: [
    {
      target: '[data-tour="tracks-menu"]',
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg blur opacity-20"></div>
              <div className="relative bg-gradient-to-r from-yellow-500 to-orange-600 p-2 rounded-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Menu de Tracks Especiais
              </h3>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">Acesso rápido e organizado!</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed">
            <strong>Novo menu lateral de tracks!</strong> Agora você tem acesso rápido aos sinistros em Fast Track e Super Fast Track através de ícones circulares no menu lateral.
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 p-2 bg-yellow-50 dark:bg-yellow-950 rounded border border-yellow-200 dark:border-yellow-800">
              <Zap className="w-3 h-3 text-yellow-600" />
              <span>Fast Track</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
              <Rocket className="w-3 h-3 text-orange-600" />
              <span>Super Fast Track</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-keffe-cream dark:bg-keffe-dark-brown rounded border border-keffe-sandstone dark:border-keffe-olive">
              <Target className="w-3 h-3 text-keffe-clay" />
              <span>Filtros dedicados</span>
            </div>
            <div className="flex items-center gap-1 p-2 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800">
              <Filter className="w-3 h-3 text-purple-600" />
              <span>Organização</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <MousePointer2 className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 dark:text-green-200">
              <strong>Navegação:</strong> Clique nos ícones laterais para acessar cada track!
            </span>
          </div>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
    }
  ]
}

/**
 * 📖 REGISTRO DE TODOS OS TOURS
 * Centraliza todos os tours disponíveis no sistema
 */
export const ALL_TOURS: Record<string, TourDefinition> = {
  [TOUR_IDS.CLAIM_DETAILS_ATTACHMENTS]: claimDetailsAttachmentsTour,
  [TOUR_IDS.TRASH_PAGE_TABS]: trashPageTabsTour,
  [TOUR_IDS.PRESTADORES_MANAGER]: prestadoresManagerTour,
  [TOUR_IDS.MAP_PRESTADORES]: mapPrestadoresTour,
  [TOUR_IDS.PHOTO_CAPTIONS]: photoCaptionsTour,
  [TOUR_IDS.CLAIMS_FILTERS]: claimsFiltersTour,
  [TOUR_IDS.COLUMN_CONFIGURATOR]: columnConfiguratorTour,
  [TOUR_IDS.DISTRIBUTION_BUTTON]: distributionButtonTour,
  [TOUR_IDS.TRACKS_MENU]: tracksMenuTour,
}

/**
 * 🔍 UTILITÁRIOS PARA TOURS
 */
export const TourUtils = {
  // Obter tour por ID
  getTour: (tourId: string): TourDefinition | undefined => {
    return ALL_TOURS[tourId]
  },

  // Listar todos os IDs de tours
  getAllTourIds: (): string[] => {
    return Object.keys(ALL_TOURS)
  },

  // Verificar se tour existe
  tourExists: (tourId: string): boolean => {
    return tourId in ALL_TOURS
  },

  // Obter tours que podem auto-iniciar
  getAutoStartTours: (): TourDefinition[] => {
    return Object.values(ALL_TOURS).filter(tour => tour.autoStart)
  }
}
